<!-- src/pages/LandingPageAirportTransfer.vue -->
<template>
  <div class="min-h-screen bg-[#0a0d0f] text-white overflow-x-hidden">
    <!-- ===== HERO ===== -->
    <section
      class="relative"
      :style="{
        backgroundImage: `linear-gradient(rgba(10,13,15,.75), rgba(10,13,15,.75)), url(${heroImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }"
    >
      <!-- top bar -->
      <div class="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 md:px-6">
        <div class="flex items-center gap-3">
          <span class="inline-block h-3 w-3 rounded-full bg-emerald-500"></span>
          <span class="text-sm font-semibold">Vehitra World</span>
        </div>
        <div class="text-[11px] opacity-80">24/7 Support · +973 3214 1440</div>
      </div>

      <div class="mx-auto grid max-w-6xl grid-cols-1 gap-8 px-4 py-8 md:grid-cols-2 md:px-6 md:py-14">
        <!-- Left: headline -->
        <div class="flex flex-col justify-center">
          <span class="mb-3 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[11px] font-semibold text-emerald-300 ring-1 ring-white/15">
            New <span class="text-white/80">Airport Transfers • Chauffeur</span>
          </span>

          <h1 class="leading-tight">
            <span class="block text-4xl font-extrabold md:text-5xl">Premium Airport</span>
            <span class="block text-4xl font-extrabold md:text-5xl">Transfers — <span class="text-emerald-400">24/7</span></span>
            <span class="mt-1 block text-4xl font-extrabold text-emerald-300 md:text-5xl">Worldwide</span>
          </h1>

          <p class="mt-4 max-w-xl text-sm text-white/80 md:text-base">
            Professional chauffeurs, flight monitoring, meet &amp; greet, and fixed upfront
            pricing. Get an instant quote now.
          </p>

          <div class="mt-5 flex flex-wrap gap-3">
            <Badge icon="lifebuoy">24/7 Support</Badge>
            <Badge icon="smile">Meet &amp; Greet</Badge>
            <Badge icon="plane">Flight Monitoring</Badge>
          </div>
        </div>

        <!-- ===== Right: frosted form card ===== -->
        <div class="lead-card-wrap">
          <div class="lead-card">
            <h2 class="text-[20px] font-semibold">Get Instant Quote</h2>
            <p class="mt-1 text-[12px] text-white/70">We’ll call or WhatsApp you to confirm.</p>

            <form
              @submit.prevent="submitLead"
              class="lead-form mt-5 grid grid-cols-1 gap-6 md:grid-cols-2"
            >
              <!-- Name / Phone -->
              <TextField label="Full Name*" placeholder="e.g. John Doe" v-model="form.name" />
              <TextField label="Phone (with country code)*" placeholder="+966 5X XXX XXXX" v-model="form.phone" />

              <!-- Email full width -->
              <div class="md:col-span-2">
                <TextField label="Email" placeholder="name@company.com" v-model="form.email" />
              </div>

              <!-- Pickup + Drop -->
              <div class="md:col-span-2 grid grid-cols-1 gap-6 md:grid-cols-2">
                <div class="field-reset min-w-0">
                  <LocationField
                    class="field-reset af-loc"
                    :layout="'simple'"
                    :drive="'distance'"
                    :hasError="false"
                    :data="pickUp"
                    type="pickUp"
                    label="Pickup Location*"
                    @addressSelected="addr => onAddressSelected(addr, 'pickUp')"
                  />
                </div>
                <div class="field-reset min-w-0">
                  <LocationField
                    class="field-reset af-loc"
                    :layout="'simple'"
                    :drive="'distance'"
                    :hasError="false"
                    :data="dropOff"
                    type="dropOff"
                    label="Drop Location*"
                    @addressSelected="addr => onAddressSelected(addr, 'dropOff')"
                  />
                </div>
              </div>

              <!-- Date / Time -->
              <div class="md:col-span-2 grid grid-cols-1 gap-6 md:grid-cols-2">
                <div class="field-reset">
                  <DateField
                    class="field-reset af-dt"
                    :layout="'simple'"
                    :hasError="false"
                    :data="pickUp.date"
                    @dateChanged="onDateChanged"
                  />
                </div>
                <div class="field-reset">
                  <TimeField
                    class="field-reset af-dt"
                    :layout="'simple'"
                    :hasError="false"
                    :data="pickUp.time"
                    @timeChanged="onTimeChanged"
                  />
                </div>
              </div>

              <!-- NEW: Car Type / Passengers / Suitcases -->
              <SelectField
                class="md:col-span-2 md:max-w-none"
                label="Car Type"
                v-model="form.carType"
                :options="carTypeOptions"
                placeholder="Select car type"
              />
              <NumberField label="Passengers" v-model.number="form.passengers" min="0" />
              <NumberField label="Suitcases" v-model.number="form.suitcases" min="0" />

              <!-- Notes -->
              <div class="md:col-span-2">
                <TextArea
                  label="Additional Notes (optional)"
                  placeholder="Flight number, child seats, special requests..."
                  v-model="form.notes"
                />
              </div>

              <!-- CTA -->
              <div class="md:col-span-2">
                <button type="submit" :disabled="submitting" class="btn-primary">
                  {{ submitting ? 'Submitting…' : 'Get Quote Now' }}
                </button>
                <p class="mt-2 text-center text-[11px] text-white/60">
                  By submitting, you agree to be contacted about your booking request.
                </p>
              </div>
            </form>
          </div>
        </div>
        <!-- ===== /Right ===== -->
      </div>
    </section>

    <!-- ===== SOCIAL PROOF ===== -->
    <section class="bg-[#0e1114] px-6 pb-6 pt-8">
      <div class="mx-auto max-w-6xl text-center">
        <span class="rounded-full bg-white/5 px-3 py-1 text-[11px] text-white/70 ring-1 ring-white/10">
          Trusted by business travelers in 65,000+ destinations
        </span>
        <div class="mt-3 text-sm text-white/60">★★★★★ <span class="align-middle">4.8/5 average rating</span></div>
      </div>
    </section>

    <!-- ===== FEATURES ===== -->
    <section class="bg-[#0b0f11] px-6 pb-10">
      <div class="mx-auto grid max-w-6xl grid-cols-1 gap-4 md:grid-cols-4">
        <FeatureCard title="Professional Chauffeurs" copy="Experienced, multilingual, trained in courtesy and safety." />
        <FeatureCard title="Meet &amp; Greet" copy="We wait at arrivals with a name board, track your flight, adjust for delays." />
        <FeatureCard title="Confirm your Quote" copy="Confirm your quote before the ride." />
        <FeatureCard title="24/7 Support" copy="Always available for last-minute changes or special requests." />
      </div>
    </section>

    <!-- ===== TESTIMONIALS ===== -->
    <section class="bg-[#0e1114] px-6 pb-10">
      <div class="mx-auto grid max-w-6xl grid-cols-1 gap-4 md:grid-cols-3">
        <Testimonial text="Flawless airport pickup, courteous driver, clean SUV. Will book again." who="— Business Consultant, Riyadh" />
        <Testimonial text="Impeccable meet & greet and smooth ride to hotel. Highly recommended." who="— Frequent Flyer, London" />
        <Testimonial text="On time, flight delay managed perfectly. Excellent service." who="— Corporate Traveler, Dubai" />
      </div>
    </section>

    <!-- ===== FAQ ===== -->
    <section class="bg-[#0b0f11] px-6 py-10">
      <div class="mx-auto max-w-6xl">
        <h3 class="mb-6 text-xl font-semibold">Frequently Asked Questions</h3>

        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FAQ q="Do you monitor flights?" a="Yes. We track live flight information and adjust pickup time if your flight is delayed." />
          <FAQ q="What’s your cancellation policy?" a="Free cancellation for most bookings up to 24 hours before pickup unless stated otherwise." />
          <FAQ q="Can I request child seats?" a="Absolutely. Add the request in notes and we’ll confirm availability in your quote." />
          <FAQ q="Which vehicles do you offer?" a="Sedans, premium sedans, luxury, SUVs and vans. We’ll match your party and luggage." />
        </div>
      </div>
    </section>

    <!-- ===== CTA ===== -->
    <section class="bg-[#0b0f11] px-6 pb-16">
      <div class="mx-auto max-w-6xl text-center">
        <h4 class="text-2xl font-extrabold">Your comfort, our priority.</h4>
        <p class="mx-auto mt-2 max-w-xl text-white/70">
          Book your airport transfer now and travel stress-free.
        </p>
        <button @click="scrollToForm" class="mt-5 inline-flex items-center justify-center rounded-full bg-emerald-500 px-6 py-3 font-semibold text-[#0a0d0f] transition hover:bg-emerald-400">
          Get Instant Quote
        </button>

        <div class="mt-8 text-[11px] text-white/40">
          © 2025 Vehitra World — Privacy • Terms • Contact
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref, defineComponent, h, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import LocationField from '@/components/search/LocationField.vue'
import DateField from '@/components/search/DateField.vue'
import TimeField from '@/components/search/TimeField.vue'
import { getBackendBaseUrl } from '@/utils/common'

/** Replace with your asset path if different */
import heroImageUrl from '@/assets/landing/airport-hero.jpg'
const heroImage = heroImageUrl

const router = useRouter()
const submitting = ref(false)
const carTypeOptions = ['Premium', 'Luxury', 'SUV', 'Van']

const form = reactive({
  name: '',
  phone: '',
  email: '',
  pickup: '',
  dropoff: '',
  datetime: '',
  notes: '',
  // NEW
  carType: '',
  passengers: 0,
  suitcases: 0,
})

// Use the same shape your Home form expects
const pickUp = reactive({
  location: { id: '', name: '', isAirport: false, coordinates: { lat: null, lng: null } },
  date: null as string | null,
  time: '' as string
})
const dropOff = reactive({
  location: { id: '', name: '', isAirport: false, coordinates: { lat: null, lng: null } }
})

const onAddressSelected = (address: any, type: 'pickUp' | 'dropOff') => {
  const add = address ?? { id: '', name: '', coordinates: { lat: null, lng: null } }
  if (type === 'pickUp') {
    pickUp.location = add
    form.pickup = add?.name || add?.description || ''
  } else {
    dropOff.location = add
    form.dropoff = add?.name || add?.description || ''
  }
}
const onDateChanged = (d: string) => {
  pickUp.date = d
  form.datetime = [pickUp.date, pickUp.time].filter(Boolean).join(' ')
}
const onTimeChanged = (t: string) => {
  pickUp.time = t
  form.datetime = [pickUp.date, pickUp.time].filter(Boolean).join(' ')
}

const scrollToForm = () => {
  const els = document.querySelectorAll('form')
  if (els && els[0]) els[0].scrollIntoView({ behavior: 'smooth', block: 'start' })
}

const submitLead = async () => {
  // basic validation
  if (!form.name || !form.phone || !form.pickup || !form.dropoff || !form.datetime) {
    alert('Please fill all required fields.')
    return
  }
  if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) {
    alert('Please enter a valid email.')
    return
  }
  if (form.passengers < 0 || form.suitcases < 0) {
    alert('Passengers / Suitcases cannot be negative.')
    return
  }

  const API_BASE = getBackendBaseUrl() // e.g. https://vehitraworld.com/backend/public/
  const payload = {
    name: form.name,
    phone: form.phone,
    email: form.email || null,
    pickup: form.pickup,
    dropoff: form.dropoff,
    datetime: form.datetime,
    notes: form.notes || null,
    source: 'landing-airport-transfer',
    // NEW extra info for admin:
    car_type: form.carType || null,
    passengers: Number(form.passengers) || 0,
    suitcases: Number(form.suitcases) || 0,
    // optionally include structured objects
    pickUp,
    dropOff,
  }

  submitting.value = true
  try {
    const res = await fetch(`${API_BASE}api/leads/airport-transfer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (!res.ok) {
      const msg = await res.text().catch(() => '')
      throw new Error(`Request failed (${res.status}) ${msg}`)
    }
    router.push({ name: 'ThankYou' })
  } catch (e: any) {
    alert(e?.message || 'Something went wrong while submitting your request.')
  } finally {
    submitting.value = false
  }
}

/* ---------- PAC dropdown patch (keeps your styling) ---------- */
function usePatchPacDropdown() {
  const getFocusedInput = (): HTMLInputElement | null =>
    document.activeElement?.tagName === 'INPUT'
      ? (document.activeElement as HTMLInputElement)
      : (document.querySelector('.lead-form input:focus') as HTMLInputElement | null)

  const apply = () => {
    const pacs = document.querySelectorAll<HTMLElement>('.pac-container')
    if (!pacs.length) return
    const input = getFocusedInput() || (document.querySelector('.lead-form input') as HTMLInputElement | null)
    if (!input) return
    const r = input.getBoundingClientRect()
    const desiredWidth = Math.max(r.width + 40, 560)
    pacs.forEach(pac => {
      pac.style.setProperty('position', 'fixed', 'important')
      pac.style.setProperty('left', `${r.left}px`, 'important')
      pac.style.setProperty('top', `${r.bottom}px`, 'important')
      pac.style.setProperty('width', `${desiredWidth}px`, 'important')
      pac.style.setProperty('minWidth', `${desiredWidth}px`, 'important')
      pac.style.setProperty('maxWidth', `${desiredWidth}px`, 'important')
      pac.style.setProperty('maxHeight', '480px', 'important')
      pac.style.setProperty('overflowY', 'auto', 'important')
      pac.style.setProperty('overflowX', 'hidden', 'important')
      pac.style.setProperty('zIndex', '99999', 'important')
      pac.style.setProperty('borderRadius', '12px', 'important')
      pac.style.setProperty('border', '1px solid rgba(255,255,255,.18)', 'important')
      pac.style.setProperty('background', 'rgba(15,19,23,.98)', 'important')
      pac.style.setProperty('boxShadow', '0 12px 40px rgba(0,0,0,.5)', 'important')
      pac.style.setProperty('padding', '4px 0', 'important')
    })
  }

  const onFocusIn  = () => setTimeout(() => { apply() }, 50)
  const onFocusOut = () => {}
  const onResize   = () => apply()
  const onScroll   = () => apply()

  onMounted(() => {
    document.addEventListener('focusin', onFocusIn, true)
    document.addEventListener('focusout', onFocusOut, true)
    window.addEventListener('resize', onResize)
    window.addEventListener('scroll', onScroll, true)
    const mo = new MutationObserver(apply)
    mo.observe(document.body, { childList: true, subtree: true })
  })
  onBeforeUnmount(() => {
    document.removeEventListener('focusin', onFocusIn, true)
    document.removeEventListener('focusout', onFocusOut, true)
    window.removeEventListener('resize', onResize)
    window.removeEventListener('scroll', onScroll, true)
  })
}
usePatchPacDropdown()

/* ---------- Lightweight local components ---------- */
const Badge = defineComponent({
  name: 'Badge',
  props: { icon: String },
  setup(props, { slots }) {
    const iconMap: Record<string, string> = { lifebuoy: '🛟', smile: '🙂', plane: '✈️' }
    return () =>
      h('span',
        { class: 'inline-flex items-center gap-2 rounded-full bg-white/5 px-3 py-1 text-xs ring-1 ring-white/10' },
        [ h('span', { class: 'opacity-80' }, iconMap[props.icon || ''] || '•'), slots.default?.() ]
      )
  }
})

const TextField = defineComponent({
  name: 'TextField',
  props: { label: String, modelValue: [String, Number], placeholder: String, type: { type: String, default: 'text' }, min: [String, Number] },
  emits: ['update:modelValue'],
  setup(props, { emit, attrs }) {
    const onInput = (e: any) => emit('update:modelValue', e.target.value)
    return () => h('label', { class: 'block', ...attrs }, [
      h('div', { class: 'mb-1 text-[11px] text-white/70' }, props.label),
      h('input', {
        class: 'w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm outline-none placeholder:text-white/30 focus:border-white/30',
        value: props.modelValue, placeholder: props.placeholder, onInput,
        type: props.type, min: props.min
      })
    ])
  }
})

const NumberField = defineComponent({
  name: 'NumberField',
  props: { label: String, modelValue: [String, Number], min: { type: [String, Number], default: 0 } },
  emits: ['update:modelValue'],
  setup(props, { emit, attrs }) {
    const onInput = (e: any) => emit('update:modelValue', e.target.value === '' ? '' : Number(e.target.value))
    return () => h('label', { class: 'block', ...attrs }, [
      h('div', { class: 'mb-1 text-[11px] text-white/70' }, props.label),
      h('input', {
        class: 'w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm outline-none placeholder:text-white/30 focus:border-white/30',
        value: props.modelValue, type: 'number', min: props.min, onInput
      })
    ])
  }
})

const SelectField = defineComponent({
  name: 'SelectField',
  props: { label: String, modelValue: String, options: Array as () => string[], placeholder: String },
  emits: ['update:modelValue'],
  setup(props, { emit, attrs }) {
    const onChange = (e: any) => emit('update:modelValue', e.target.value)
    return () => h('label', { class: 'block', ...attrs }, [
      h('div', { class: 'mb-1 text-[11px] text-white/70' }, props.label),
      h('select', {
        class: 'w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm outline-none focus:border-white/30',
        value: props.modelValue ?? '',
        onChange
      }, [
        h('option', { value: '' }, props.placeholder || 'Select…'),
        ...(props.options || []).map(o => h('option', { value: o }, o))
      ])
    ])
  }
})

const TextArea = defineComponent({
  name: 'TextArea',
  props: { label: String, modelValue: String, placeholder: String },
  emits: ['update:modelValue'],
  setup(props, { emit, attrs }) {
    const onInput = (e: any) => emit('update:modelValue', e.target.value)
    return () => h('label', { class: 'block', ...attrs }, [
      h('div', { class: 'mb-1 text-[11px] text-white/70' }, props.label),
      h('textarea', {
        class: 'min-h-[90px] w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm outline-none placeholder:text-white/30 focus:border-white/30',
        value: props.modelValue, placeholder: props.placeholder, onInput
      })
    ])
  }
})

const FeatureCard = defineComponent({
  name: 'FeatureCard',
  props: { title: String, copy: String },
  setup(props) {
    return () =>
      h('div', { class: 'rounded-2xl border border-white/10 bg-[#0f1317] p-5' }, [
        h('div', { class: 'text-[13px] font-semibold' }, props.title),
        h('p', { class: 'mt-2 text-sm text-white/70' }, props.copy)
      ])
  }
})

const Testimonial = defineComponent({
  name: 'Testimonial',
  props: { text: String, who: String },
  setup(props) {
    return () =>
      h('div', { class: 'rounded-2xl border border-white/10 bg-[#0f1317] p-5' }, [
        h('div', { class: 'mb-2 text-emerald-400' }, '★★★★★'),
        h('p', { class: 'text-sm text-white/85' }, `“${props.text}”`),
        h('div', { class: 'mt-3 text-xs text-white/50' }, props.who)
      ])
  }
})

const FAQ = defineComponent({
  name: 'FAQ',
  props: { q: String, a: String },
  setup(props) {
    const open = ref(false)
    const toggle = () => (open.value = !open.value)
    return () =>
      h('div', { class: 'overflow-hidden rounded-2xl border border-white/10 bg-[#0f1317]' }, [
        h('button', { class: 'flex w-full items-center justify-between px-4 py-4 text-left text-sm font-semibold', onClick: toggle },
          [ h('span', props.q), h('span', { class: (open.value ? 'rotate-90 ' : '') + 'transition' }, '▸') ]
        ),
        open.value && h('div', { class: 'px-4 pb-4 text-sm text-white/70' }, props.a)
      ])
  }
})
</script>

<style scoped>
:global(:root) {
  --lead-input-h: 56px;
  --lead-border: rgba(255,255,255,.14);
  --lead-fill: rgba(255,255,255,.06);
  --lead-focus: rgba(255,255,255,.32);
}
.lead-card-wrap { width: 100%; }
@screen md { .lead-card-wrap { @apply max-w-[560px] justify-self-end; } }

.lead-card {
  @apply rounded-2xl backdrop-blur-2xl p-6 md:p-8;
  background: linear-gradient(180deg, rgba(255,255,255,.10), rgba(255,255,255,.06));
  border: 1px solid rgba(255,255,255,.18);
  box-shadow: inset 0 1px 0 rgba(255,255,255,.12), 0 20px 60px rgba(0,0,0,.35);
}
.btn-primary {
  @apply w-full rounded-full font-semibold transition px-5 py-4 text-[#0a0d0f] bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60;
  font-size: 16px;
}

/* ===== Resets for embedded fields ===== */
.lead-form :deep(.af-loc.relative) { padding: 0 !important; overflow: visible !important; }
.lead-form :deep(.af-loc .flex.flex-col.space-y-2) { gap: 6px !important; }
.lead-form :deep(.af-loc .text-small),
.lead-form :deep(.af-loc .text-small *),
.lead-form :deep(.af-loc [class*="uppercase"]) {
  text-transform: none !important;
  font-size: 11px !important;
  line-height: 1.2 !important;
  letter-spacing: .02em !important;
  color: rgba(255,255,255,.70) !important;
  font-weight: 600 !important;
}
.lead-form :deep(.af-loc input[type="text"]) {
  height: var(--lead-input-h) !important;
  min-height: var(--lead-input-h) !important;
  border-radius: 12px !important;
  border: 1px solid var(--lead-border) !important;
  background: var(--lead-fill) !important;
  color: #fff !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  padding: 0 16px !important;
  outline: none !important; box-shadow: none !important;
}
.lead-form :deep(.af-loc .fa-times-circle),
.lead-form :deep(.af-loc .fa-circle-xmark),
.lead-form :deep(.af-loc [data-icon="times-circle"]),
.lead-form :deep(.af-loc [data-icon="circle-xmark"]),
.lead-form :deep(.af-loc .clear-btn) { display: none !important; }

.lead-form :deep(.af-loc > .relative > ul[class*="absolute"][class*="ring-"]) {
  left: 0 !important; right: auto !important; top: 100% !important;
  width: 100% !important; margin-left: 0 !important;
  max-height: 520px !important; overflow-y: auto !important; overflow-x: hidden !important;
  background: rgba(15,19,23,.98) !important;
  border: 1px solid rgba(255,255,255,.18) !important;
  border-radius: 12px !important;
  box-shadow: 0 12px 40px rgba(0,0,0,.5) !important;
  padding: 4px 0 !important; z-index: 10000 !important;
}
@media (min-width: 768px) {
  .lead-form :deep(.af-loc > .relative > ul[class*="absolute"][class*="ring-"]) {
    width: calc(100% + 40px) !important; margin-left: -20px !important;
  }
}
.lead-form :deep(.af-loc > .relative > ul[class*="absolute"][class*="ring-"] > li) {
  display: flex !important; align-items: flex-start !important; gap: 10px !important;
  padding: 12px 16px !important; border-bottom: 1px solid rgba(255,255,255,.06) !important;
  color: #d8dee9 !important; font-size: 15px !important; line-height: 1.4 !important; cursor: pointer !important;
}
.lead-form :deep(.af-loc > .relative > ul[class*="absolute"][class*="ring-"] > li:hover) { background: rgba(255,255,255,.08) !important; }
.lead-form :deep(.af-loc > .relative > ul[class*="absolute"][class*="ring-"] > li:last-child) { border-bottom: 0 !important; }
.lead-form :deep(.af-loc > .relative > ul[class*="absolute"][class*="ring-"] > li svg) {
  color: #9fb7ff !important; margin-top: 2px !important;
}

.lead-form :deep(.af-dt), .lead-form :deep(.af-dt [class*="p-"]), .lead-form :deep(.af-dt [class*="px-"]), .lead-form :deep(.af-dt [class*="py-"]), .lead-form :deep(.af-dt [class*="m-"]) {
  padding: 0 !important; margin: 0 !important;
}
.lead-form :deep(.af-dt label), .lead-form :deep(.af-dt .label), .lead-form :deep(.af-dt [class*="uppercase"]) {
  text-transform: none !important; margin-bottom: 6px !important;
  font-size: 11px !important; line-height: 1.2 !important; letter-spacing: .02em !important;
  color: rgba(255,255,255,.70) !important; font-weight: 600 !important;
}
.lead-form :deep(.af-dt input), .lead-form :deep(.af-dt input[type="text"]), .lead-form :deep(.af-dt input[type="date"]), .lead-form :deep(.af-dt input[type="time"]) {
  width: 100% !important; height: var(--lead-input-h) !important; min-height: var(--lead-input-h) !important;
  border-radius: 12px !important; border: 1px solid var(--lead-border) !important; background: var(--lead-fill) !important;
  color: #fff !important; font-size: 15px !important; font-weight: 500 !important; padding: 0 16px !important;
  outline: none !important; box-shadow: none !important; color-scheme: dark;
}
.lead-form :deep(.af-dt input::placeholder) { color: rgba(255,255,255,.38) !important; font-weight: 500 !important; }
.lead-form :deep(.af-dt input[type="date"]::-webkit-calendar-picker-indicator),
.lead-form :deep(.af-dt input[type="time"]::-webkit-calendar-picker-indicator) { filter: invert(1) opacity(.85); }
.lead-form :deep(.af-dt.relative) { overflow: visible !important; }
</style>

<style>
.pac-item { color: #d8dee9 !important; font-size: 15px !important; line-height: 1.4 !important;
  padding: 12px 16px !important; white-space: nowrap !important; overflow: hidden !important; text-overflow: ellipsis !important; }
.pac-item:hover { background: rgba(255,255,255,.08) !important; }
.pac-item-query { font-weight: 700 !important; color: #fff !important; }
.pac-icon { filter: brightness(0) invert(1) !important; margin-right: 8px !important; }
</style>